import React, { Fragment, useEffect, useMemo } from "react";
import { Avatar, Popover, Badge, Icon, List, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import * as R from "ramda";
import { compose } from "recompose";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import { Link } from "react-router-dom";
import moment from "moment-timezone";

import { httpToHttps } from "../../../../utils/images";
import { isLoading } from "../../../../utils/actions";
import { getOptimizedAvatar } from "../../../../utils/images";
import { vinculationStatuses } from "../../../List/VinculationsTable";
import { linkToCompanies } from "../../../../store/actions/company";
import { FETCH_NEW_MESSAGES } from "../../../../store/actions/chat";
import { TopLink } from "../TopLink";

import VinculationNotification from "./VinculationNotification";

import styled, { createGlobalStyle } from "styled-components/macro";

const PopoverGlobalStyles = createGlobalStyle`
  .avatar {
    img {
      object-fit: cover;
    }
  }
  .chat-notifications-popover {
    .ant-popover-inner-content {
      padding: 0;
    }

    .ant-popover-title {
      padding: 10px 12px;
    }
    .ant-tabs-ink-bar {
    bottom: inherit;
    top: 0;
    }
    .ant-tabs-tab {
      margin: 0 !important;
      min-width: 224px;
      display: inline-flex !important;
      justify-content: center;
      &:not(.ant-tabs-tab-active) {
        background: #f7f9fa;
        border-bottom: 2px solid #e5e9ec !important;
      }
    }
    .ant-avatar {
      cursor: pointer;
      transition: 0.3s ease;
      &:hover {
        box-shadow: 0 0px 4px 0 ${({ theme }) => theme["@primary"]};
      }
    }

    .notification-tabs .ant-tabs-bar {
      margin: 0;
    }
  }
`;

const StyledList = styled(List)`
  width: 450px;
  max-height: 400px;
  overflow-y: auto;

  .ant-list-item {
    padding: 0 12px;
    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.025);
    }
  }

  .ant-list-item-meta-content {
    overflow: hidden;
  }

  .ant-list-item-meta-title {
    margin-bottom: 0;
    font-weight: bold;
  }

  .ant-list-item-meta-description {
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: flex;
    align-items: center;
  }
`;

const ItemLink = styled(Link)`
  width: 100%;
`;

const Title = styled.div`
  display: flex;
  justify-content: space-between;

  .title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const TimeStamp = styled.span`
  color: #747c84;
  ${props =>
    props.isLoading &&
    `
    filter: blur(3px);
    opacity: 0.5;
  `}
`;

const TabContainer = styled.div`
  display: flex;
  align-items: center;
`;

// const ViewAll = styled(Link)`
//   display: flex;
//   align-items: center;
//   padding: 10px;
//   border: none;
//   background: none;
//   justify-content: center;
//   width: 100%;
//   cursor: pointer;
//   border-top: 1px solid #e8e8e8;
//   transition: 0.3s ease;
//   font-size: 14px;
//   color: ${({ theme }) => theme["@primary"]};
//   &:hover {
//     opacity: 0.6;
//   }
//   .anticon {
//     margin-right: 5px;
//   }
// `;

const ChatNotifications = ({
  messagesByOpportunity,
  loadingMessages,
  visible = false,
  notificationsData
}) => {
  const [showPopover, setShowPopover] = React.useState(visible);
  const { t } = useTranslation(["chatMessages", "global"]);
  const [currentTab, setCurrentTab] = React.useState(
    notificationsData.length ? "notifications" : "messages"
  );

  useEffect(() => {
    if (!visible && notificationsData.length) {
      setCurrentTab("notifications");
    }
  }, [notificationsData, visible]);

  const unreadCount = useMemo(
    () =>
      R.pipe(
        R.flatten,
        R.length,
        R.add(notificationsData.length)
      )(messagesByOpportunity),
    [messagesByOpportunity, notificationsData.length]
  );

  useEffect(() => {
    if (
      !visible &&
      R.pipe(
        R.flatten,
        R.length
      )(messagesByOpportunity)
    ) {
      setCurrentTab("messages");
    }
  }, [messagesByOpportunity, visible]);

  return (
    <Fragment>
      <PopoverGlobalStyles />
      <Popover
        overlayClassName="chat-notifications-popover"
        visible={showPopover}
        onVisibleChange={setShowPopover}
        content={
          <Tabs
            defaultActiveKey={currentTab}
            activeKey={currentTab}
            onChange={setCurrentTab}
            animated={false}
            className="notification-tabs"
          >
            <Tabs.TabPane
              tab={
                <TabContainer>
                  <Icon type="message" />
                  {t("global:messages")}
                </TabContainer>
              }
              key={`messages`}
              activeKey={`messages`}
            >
              <StyledList
                data-test="new-messages-list"
                loading={loadingMessages}
                locale={{
                  emptyText: t("chatMessages:noMessages")
                }}
                itemLayout="horizontal"
                dataSource={messagesByOpportunity}
                renderItem={messages => {
                  const firstMessage = messages[0];
                  const {
                    idOpportunity,
                    guid,
                    text,
                    idCandidate,
                    candidateName,
                    candidateAvatar,
                    opportunityTitle,
                    timestamp
                  } = firstMessage;

                  return (
                    <List.Item
                      key={guid}
                      data-test="new-message"
                      style={{ padding: 12 }}
                    >
                      <ItemLink
                        onClick={() => setShowPopover(false)}
                        to={`/oportunidades/${idOpportunity}/proceso/${idCandidate}/chat`}
                      >
                        <List.Item.Meta
                          avatar={
                            <Badge count={messages.length} offset={[-5, 5]}>
                              <Avatar
                                size={48}
                                src={getOptimizedAvatar(candidateAvatar)}
                              />
                            </Badge>
                          }
                          title={
                            <Title>
                              <span
                                data-test="new-message-title"
                                className="title"
                              >{`${candidateName} - ${opportunityTitle}`}</span>
                              <TimeStamp isLoading={loadingMessages}>
                                {moment(timestamp).isSame(Date.now(), "day")
                                  ? moment(timestamp).format("HH:mm")
                                  : moment(timestamp).isSame(Date.now(), "year")
                                  ? moment(timestamp).format("MMM D")
                                  : moment(timestamp).format("L")}
                              </TimeStamp>
                            </Title>
                          }
                          description={
                            <span data-test="new-message-text">{text}</span>
                          }
                        />
                      </ItemLink>
                    </List.Item>
                  );
                }}
              />
            </Tabs.TabPane>

            <Tabs.TabPane
              tab={
                <TabContainer>
                  <Icon type="notification" />
                  {t("notifications:notifications")}
                </TabContainer>
              }
              key={`notifications`}
              activeKey={`notifications`}
            >
              <StyledList
                data-test="new-notifications-list"
                // loading={loadingNotifications}
                locale={{
                  emptyText: t("notifications:empty")
                }}
                itemLayout="horizontal"
                rowKey={notification => {
                  const { timestamp, companyId } = notification;
                  return timestamp || companyId;
                }}
                dataSource={notificationsData || []}
                renderItem={notification => {
                  return (
                    <VinculationNotification
                      notification={notification}
                      setShowPopover={setShowPopover}
                    />
                  );
                }}
              />
              {/* <ViewAll to="/compañia/vinculations">
                  <Icon type="arrow-right" />
                  {t("chatMessages:viewAllNotifications")}
                </ViewAll> */}
            </Tabs.TabPane>
          </Tabs>
        }
        trigger={"click"}
        placement="bottomRight"
        arrowPointAtCenter={true}
      >
        <TopLink
          data-test="chat-notifications-btn"
          title={t("global:notifications")}
        >
          <Badge
            data-test="new-messages-badge"
            count={unreadCount}
            offset={[0, 0]}
          >
            <div style={{ fontSize: 24, textAlign: "center" }}>
              <Icon type="bell" />
            </div>
          </Badge>
        </TopLink>
      </Popover>
    </Fragment>
  );
};

const newMessagesSelector = createSelector(
  [state => R.pathOr([], ["messages", "newMessages"], state)],
  newMessages =>
    R.pipe(
      R.groupBy(message => `${message.idOpportunity}/${message.idCandidate}`),
      R.toPairs,
      // Order messages by timestamp to show text from latest message
      R.map(([, messages]) => R.sort(R.descend(R.prop("timestamp")), messages)),
      // Order opportunities by last message timestamp
      R.sort(R.descend(R.path([0, "timestamp"])))
    )(newMessages)
);

const enhancer = compose(
  connect(
    state => ({
      messagesByOpportunity: newMessagesSelector(state),
      notificationsData: R.pipe(
        R.pathOr([], ["company", "companyVinculations", "requiredToMe"]),
        R.filter(R.propEq("status", vinculationStatuses.pending)),
        R.map(({ name, logo, idCompany, updatedAt }) => ({
          type: "NEW_LINKING_REQUEST",
          companyName: name,
          companyLogo: httpToHttps(logo),
          companyId: idCompany,
          timestamp: updatedAt
        }))
      )(state),
      loadingMessages: isLoading(FETCH_NEW_MESSAGES, state)
    }),
    {
      linkToCompanies
    }
  )
);

export default enhancer(ChatNotifications);
